/**
 * Port Mapping Types
 *
 * Defines the structure for port mapping templates and rules
 * that control how equipment ports are connected in cable generation
 */

/**
 * Strategy for mapping ports between equipment
 */
export enum PortMappingStrategy {
  /** Map port 1 to port 1, port 2 to port 2, etc. */
  OneToOne = 'one-to-one',
  /** Map all source ports to port 1 of destination */
  AllToFirst = 'all-to-first',
  /** Map port 1 of source to all destination ports */
  FirstToAll = 'first-to-all',
  /** Custom mapping defined by explicit rules */
  Custom = 'custom',
}

/**
 * A single port-to-port connection mapping
 */
export interface PortConnection {
  /** Source equipment port number */
  sourcePort: number
  /** Destination equipment port number */
  destinationPort: number
  /** Optional: Destination equipment instance number (for expanded master equipment) */
  destinationInstanceNumber?: number
}

/**
 * Rule defining how to map ports between two pieces of equipment
 */
export interface PortMappingRule {
  /** Unique identifier for this rule */
  id: string
  /** Source equipment definition ID (from equipment template) */
  sourceEquipmentId: string
  /** Destination equipment definition ID (from equipment template) */
  destinationEquipmentId: string
  /** Mapping strategy to use */
  strategy: PortMappingStrategy
  /** Explicit port connections (used for Custom strategy) */
  connections?: PortConnection[]
  /** Description of this mapping rule */
  description?: string
}

/**
 * A named template containing multiple port mapping rules
 */
export interface PortMappingTemplate {
  /** Unique identifier for this template */
  id: string
  /** Display name for this template */
  name: string
  /** Description of this template's purpose */
  description?: string
  /** List of port mapping rules in this template */
  rules: PortMappingRule[]
  /** When this template was created */
  createdAt: Date
  /** When this template was last modified */
  updatedAt: Date
}

/**
 * Port mapping state for a survey
 */
export interface SurveyPortMapping {
  /** Survey ID this mapping applies to */
  surveyId: string
  /** Currently active template ID */
  activeTemplateId: string | null
  /** All available templates for this survey */
  templates: PortMappingTemplate[]
}

/**
 * Structure for exporting/importing port mapping files
 */
export interface PortMappingFileData {
  /** File format version */
  version: string
  /** Survey ID this mapping was created for */
  surveyId: string
  /** Survey name for reference */
  surveyName: string
  /** When this file was exported */
  exportedAt: string
  /** Templates to export/import */
  templates: PortMappingTemplate[]
  /** Active template ID at time of export */
  activeTemplateId: string | null
}

/**
 * Result of applying port mapping to a cable connection
 */
export interface CablePortMapping {
  /** Cable ID this mapping applies to */
  cableId: string
  /** Source equipment instance ID */
  sourceEquipmentInstanceId: string
  /** Destination equipment instance ID */
  destinationEquipmentInstanceId: string
  /** Port connections for this cable */
  connections: PortConnection[]
  /** Template ID that generated this mapping */
  templateId: string
  /** Rule ID that was applied */
  ruleId: string
}

/**
 * Physical port assignment for a cable endpoint
 * Tracks which specific port number a cable occupies on an equipment instance
 */
export interface CableEndpointPortAssignment {
  /** Cable ID */
  cableId: string
  /** Equipment instance ID (space equipment ID) */
  equipmentInstanceId: string
  /** Equipment definition ID */
  equipmentDefinitionId: string
  /** Equipment instance number (e.g., 1 for "Master Patch Panel 01", 2 for "Master Patch Panel 02") */
  instanceNumber: number
  /** Physical port number on this instance (1-based) */
  portNumber: number
  /** Whether this is connected01 or connected02 of the cable */
  connectionEnd: 'connected01' | 'connected02'
}

/**
 * Complete port assignment for a cable (both endpoints)
 */
export interface CablePortAssignment {
  /** Cable ID */
  cableId: string
  /** Source (connected01) port assignment */
  sourcePort: CableEndpointPortAssignment | null
  /** Destination (connected02) port assignment */
  destinationPort: CableEndpointPortAssignment | null
}

/**
 * Port allocation state for an equipment instance
 * Tracks which cables are using which ports
 */
export interface EquipmentPortAllocation {
  /** Equipment instance ID (space equipment ID) */
  equipmentInstanceId: string
  /** Equipment definition ID */
  equipmentDefinitionId: string
  /** Equipment instance number (for expanded equipment) */
  instanceNumber: number
  /** Total ports available on this instance */
  totalPorts: number
  /** Ports currently in use */
  usedPorts: number
  /** Map of port number to cable ID using that port */
  portAssignments: Map<number, string>
  /** Next available port number (1-based) */
  nextAvailablePort: number
}
